import { WAITLIST_LINE, WaitlistButton } from "@/components/Waitlist";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { IMG, SiteNav, SiteFooter, ProofRoll, ClientRoll } from "@/components/SiteChrome";
import { RootsLine, RootsCorner } from "@/components/Roots";

const WAYS = [
  ["Fractional", "Senior growth leadership a few days a month, without the cost of a full-time hire."],
  ["Consultant", "A focused diagnostic and clear direction when you're at a turning point."],
  ["Project", "A defined piece of work: a launch, a brand foundation or a retail pitch."],
];

export const useClubInfo = () => {
  const [d, setD] = useState({ testimonials: [], stats: [], clients: [], contact_email: "" });
  useEffect(() => { api.get("/public/club").then((r) => setD(r.data)).catch(() => {}); }, []);
  return d;
};

const Hero = () => (
  <section className="relative overflow-hidden bg-navy text-white">
    <div className="absolute inset-0 lg:left-auto lg:right-0 lg:aspect-square">
      <img src={IMG.lindsay} alt="Lindsay Fenn, founder of Roots Marketing Co" data-testid="home-hero-photo"
        className="h-full w-full object-cover object-[50%_20%] opacity-40 lg:object-center lg:opacity-60" />
      <div className="hidden lg:block absolute inset-y-0 left-0 w-3/5 bg-gradient-to-r from-navy via-navy/70 to-transparent" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/30 lg:hidden" />
    <RootsCorner />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-36">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-sun mb-6 fade-up">Food · Drink · Hospitality. Nothing else.</div>
      <h1 className="font-serif uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.95] max-w-4xl fade-up" data-testid="home-hero-title">Marketing that grows from strong roots.</h1>
      <p className="mt-6 max-w-xl text-base sm:text-lg text-white/85 fade-up">I'm Lindsay Fenn. For 20 years I've helped food and drink brands, restaurants and bars grow, from first market stall to supermarket shelf. The Roots Club puts that expertise in your pocket.</p>
      <div className="mt-10 flex flex-wrap gap-3 fade-up">
        <WaitlistButton testId="home-hero-join" className="px-7 py-3.5" />
        <a href="#work-with-me" data-testid="home-hero-work" className="rounded-full border border-white/40 hover:border-sun hover:text-sun px-7 py-3.5 transition-colors">Work with me</a>
      </div>
      <p className="mt-4 max-w-lg text-sm text-white/75 fade-up" data-testid="home-hero-waitlist-line">{WAITLIST_LINE}</p>
    </div>
  </section>
);

const About = () => (
  <section className="mx-auto max-w-7xl px-6 lg:px-10 py-24 grid gap-12 lg:grid-cols-12 items-center">
    <div className="lg:col-span-5 grid grid-cols-2 gap-4">
      <img src={IMG.event1} alt="A brand event Lindsay delivered" className="rounded-2xl aspect-[3/4] object-cover" />
      <img src={IMG.own1} alt="Food and drink work by Roots Marketing Co" className="rounded-2xl aspect-[3/4] object-cover mt-10" />
    </div>
    <div className="lg:col-span-7">
      <div className="eyebrow mb-3">About Lindsay</div>
      <h2 className="font-serif uppercase text-3xl sm:text-4xl text-navy leading-tight">I've sat on every side of the table.</h2>
      <RootsLine className="w-48 h-8 text-leaf/60 my-4" />
      <div className="space-y-4 text-base leading-relaxed text-foreground/85 max-w-2xl">
        <p>Brand side, distributor side and agency side. I've launched brands from new-to-market to supermarket shelf, built on-trade partnerships for drinks brands, and brought together a festival of more than 200 restaurants.</p>
        <p>I work foundations-first. Most growth problems don't come from a lack of activity. They come from weak foundations. Get clear, plan, then grow in the right order. That's the Roots Growth Model, and it's built into everything I do.</p>
      </div>
    </div>
  </section>
);

const Stats = ({ stats }) => stats.length > 0 && (
  <section className="bg-sun/90" data-testid="home-stats">
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
      {stats.map((s, i) => (
        <div key={s.label} data-testid={`home-stat-${i}`} className={i === 0 ? "sm:col-span-2 lg:col-span-1 lg:row-span-1" : ""}><div className={`font-serif text-navy ${i === 0 ? "text-7xl lg:text-8xl" : "text-5xl lg:text-6xl"}`}>{s.value}</div><p className="mt-2 text-sm text-navy/80 max-w-[16rem]">{s.label}</p></div>
      ))}
    </div>
  </section>
);

const Ways = ({ email }) => (
  <section id="work-with-me" className="mx-auto max-w-7xl px-6 lg:px-10 py-24" data-testid="work-with-me">
    <div className="eyebrow mb-3">How to work with me</div>
    <h2 className="font-serif uppercase text-3xl sm:text-4xl text-navy mb-10">Four ways in.</h2>
    <div className="grid gap-5 lg:grid-cols-5">
      <div data-testid="way-club" className="relative overflow-hidden lg:col-span-2 lg:row-span-3 rounded-3xl bg-navy text-white p-8 lg:p-10 flex flex-col justify-between min-h-[320px] group">
        <img src={IMG.cafe} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25 group-hover:scale-105 transition-transform duration-700" />
        <div className="relative"><div className="text-xs font-mono uppercase tracking-[0.2em] text-sun">Launching soon</div>
          <h3 className="font-serif uppercase text-4xl mt-3">Join The Roots Club</h3>
          <p className="mt-3 text-white/85 max-w-sm">The marketing expert in your pocket. On-demand answers, templates and seasonal ideas built on my 20 years in the trade.</p></div>
        <div className="relative mt-8"><WaitlistButton testId="way-club-waitlist" className="px-6 py-3" line lineClass="text-white/80" />
          <Link to="/the-roots-club" data-testid="way-club-more" className="mt-3 inline-flex items-center gap-1 text-sm text-sun hover:underline">See what's inside <ArrowRight className="h-3.5 w-3.5" /></Link></div>
      </div>
      {WAYS.map(([t, d]) => (
        <div key={t} className="lg:col-span-3 rounded-2xl border border-border bg-card p-6 flex items-start gap-4" data-testid={`way-${t.toLowerCase()}`}>
          <span className="mt-2 h-2 w-2 rounded-full bg-leaf shrink-0" />
          <div><h3 className="font-serif uppercase text-xl text-navy">{t}</h3><p className="text-sm text-muted-foreground mt-1">{d}</p></div>
        </div>
      ))}
    </div>
    {email && <p className="mt-8 text-sm flex items-center gap-2"><Mail className="h-4 w-4 text-pinkb" />For consultancy enquiries, email <a href={`mailto:${email}`} className="text-pinkb underline" data-testid="consultancy-email">{email}</a></p>}
  </section>
);

export default function Home() {
  const d = useClubInfo();
  return (
    <div className="relative z-10 min-h-screen bg-background">
      <SiteNav />
      <Hero />
      <ClientRoll clients={d.clients} />
      <About />
      <Stats stats={d.stats} />
      <section className="py-16" data-testid="home-testimonials"><div className="mx-auto max-w-7xl px-6 lg:px-10 eyebrow mb-4">What people say</div>
        <ProofRoll items={d.testimonials} />
      </section>
      <Ways email={d.contact_email} />
      <SiteFooter email={d.contact_email} />
    </div>
  );
}
